import { apiService } from './api.service';
import { PaginatedResponse } from 'types/index';
import { User } from 'types/user';
import api_no_token_Service from './api_no_token.service';

export type UserListOptions = {
  search?: string;
  emailSearch?: string;
};

class UsersService {
  async list(
    page: number = 1,
    limit: number = 100,
    options?: UserListOptions
  ): Promise<PaginatedResponse<User>> {
    const params = new URLSearchParams({
      page: String(page),
      pageSize: String(limit),
    });
    if (options?.search?.trim()) {
      params.set('search', options.search.trim());
    }
    if (options?.emailSearch?.trim()) {
      params.set('emailSearch', options.emailSearch.trim());
    }

    const response = await apiService.get<PaginatedResponse<User>>(
      `/user/GetPageList?${params.toString()}`
    );

    if ((response as any)?.data && (response as any)?.success !== false && (response as any).data?.data && (response as any).data?.pagination) {
      return (response as any).data as PaginatedResponse<User>;
    }

    const root = response as any;
    const maybeItems = root?.items ?? root?.Items;
    const maybeTotal = root?.totalCount ?? root?.TotalCount;
    if (Array.isArray(maybeItems) && typeof maybeTotal === 'number') {
      const totalPages = Math.max(1, Math.ceil(maybeTotal / limit));
      return {
        data: maybeItems as User[],
        pagination: { page, limit, total: maybeTotal, totalPages },
      };
    }

    const payload = root?.data ?? root;
    if (payload?.items && typeof payload.totalCount === 'number') {
      const totalPages = Math.max(1, Math.ceil(payload.totalCount / limit));
      return {
        data: payload.items as User[],
        pagination: {
          page,
          limit,
          total: payload.totalCount,
          totalPages,
        },
      };
    }
    if (payload?.Items && typeof payload.TotalCount === 'number') {
      const totalPages = Math.max(1, Math.ceil(payload.TotalCount / limit));
      return {
        data: payload.Items as User[],
        pagination: {
          page,
          limit,
          total: payload.TotalCount,
          totalPages,
        },
      };
    }

    const data = payload;
    if (Array.isArray(data)) {
      return {
        data: data as User[],
        pagination: { page, limit, total: data.length, totalPages: 1 },
      };
    }

    throw new Error((response as any)?.message || 'Failed to load users');
  }

  async getById(id: string): Promise<User> {
    const response = await apiService.get<any>(`/user/${id}`);
    const payload: any = (response as any)?.data ?? response;
    const userData = payload?.data && typeof payload.data === 'object' ? payload.data : typeof payload === 'object' ? payload : null;
    if (userData) return userData as User;
    return response as unknown as User;
  }

  async getRegisterData(id: string): Promise<{ user: User; company: any | null }> {
    const response = await api_no_token_Service.get<any>(`/user/GetRegisterData/${id}`);
    const payload: any = (response as any)?.data ?? response;
    
    if (payload?.success && payload?.user) {
      return {
        user: payload.user as User,
        company: payload.company || null
      };
    }
    
    throw new Error(payload?.error || 'Failed to load register data');
  }

  async create(payload: Partial<User>): Promise<User> {
    const response = await apiService.post<User>(`/user`, payload);
    if ((response as any)?.data && (response as any)?.success !== false) {
      return (response as any).data as User;
    }
    return (response as any) as User;
  }

  async createUser(payload: Partial<User>): Promise<User> {
    const response = await api_no_token_Service.post<User>(`/user/CreateRegistrationAccount`, payload);
    if ((response as any)?.data && (response as any)?.success !== false) {
      return (response as any).data as User;
    }
    return (response as any) as User;
  }

  async update(id: string, payload: Partial<User>): Promise<User> {
    const response = await apiService.put<User>(`/user/${id}`, payload);
    if ((response as any)?.data && (response as any)?.success !== false) {
      return (response as any).data as User;
    }
    return (response as any) as User;
  }

  async remove(id: string): Promise<void> {
    const response = await apiService.delete(`/user/${id}`);
    if ((response as any)?.success === false) {
      throw new Error((response as any)?.message || 'Delete failed');
    }
  }
}

export const usersService = new UsersService();
export default usersService;


